/**
 * Cook Log Sync Service
 *
 * Persists the cook journal (cookLogSlice) to Firestore so it survives reloads
 * and syncs across devices. One document per recipe.
 *
 * Path: households/{hid}/cookLog/{recipeId} when paired,
 *       users/{uid}/cookLog/{recipeId}      when solo.
 *
 * Entries live in a map keyed by entry id (`byId`), and every write touches
 * exactly one key. The document used to hold one `entries` array rewritten
 * whole on every change, so a device that had loaded the journal at boot and
 * then logged a cook republished its stale array over whatever the other
 * partner had added since — and the journal has no live listener, so "since"
 * was the whole session. Documents written that way are still read
 * (`entries` ∪ `byId`); nothing writes the array any more except to remove an
 * entry that only exists there.
 */

import {
  doc,
  collection,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteField,
  FieldPath,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { firestore, isFirebaseEnabled } from './firebaseConfig';
import { resolveDataScope, buildScopedPath } from './householdPaths';
import type { CookLogEntry } from '../redux/cookLogSlice';

interface FirestoreCookLogDoc {
  recipeId: string;
  /** Written before #795: the whole journal of the recipe, rewritten on every change. */
  entries?: CookLogEntry[];
  /** One key per entry. */
  byId?: Record<string, CookLogEntry>;
  updatedAt: Timestamp | string;
}

const getCookLogCollection = (userId: string, householdId?: string | null) => {
  if (!firestore) {
    throw new Error('Firestore not initialized');
  }
  const scope = resolveDataScope(userId, householdId);
  if (!scope) {
    throw new Error('Cannot resolve data scope: missing userId and householdId');
  }
  const [root, id, ...rest] = buildScopedPath(scope, 'cookLog');
  return collection(firestore, root, id, ...rest);
};

const getCookLogDocRef = (
  userId: string,
  recipeId: string,
  householdId?: string | null
) => {
  if (!firestore) {
    throw new Error('Firestore not initialized');
  }
  const scope = resolveDataScope(userId, householdId);
  if (!scope) {
    throw new Error('Cannot resolve data scope: missing userId and householdId');
  }
  const [root, id, ...rest] = buildScopedPath(scope, 'cookLog', recipeId);
  return doc(firestore, root, id, ...rest);
};

/**
 * Firestore refuses `undefined` (this app does not enable
 * ignoreUndefinedProperties), and an entry logged without a note or verdict
 * carries both as undefined — which is how a note-less cook never reached the
 * cloud before #795.
 */
export const toCloudEntry = (entry: CookLogEntry): CookLogEntry =>
  Object.fromEntries(
    Object.entries(entry).filter(([, value]) => value !== undefined)
  ) as unknown as CookLogEntry;

/** Both shapes a document may carry, as one list. `byId` wins on a shared id. */
export const entriesOfCookLogDoc = (data: Partial<FirestoreCookLogDoc>): CookLogEntry[] => {
  const byId = data.byId && typeof data.byId === 'object' ? data.byId : {};
  const legacy = Array.isArray(data.entries) ? data.entries : [];
  return [
    ...legacy.filter(e => e && typeof e.id === 'string' && !(e.id in byId)),
    ...Object.values(byId).filter(e => e && typeof e.id === 'string'),
  ];
};

/** Write one entry, leaving every other entry of the recipe alone. */
export const putCookLogEntryInCloud = async (
  userId: string,
  entry: CookLogEntry,
  householdId?: string | null
): Promise<void> => {
  if (!isFirebaseEnabled() || !firestore) {
    throw new Error('Firebase not enabled');
  }
  const docRef = getCookLogDocRef(userId, entry.recipeId, householdId);
  // A merge set deep-merges maps, so only this key of `byId` is written.
  await setDoc(
    docRef,
    {
      recipeId: entry.recipeId,
      byId: { [entry.id]: toCloudEntry(entry) },
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
};

/** Remove one entry, wherever the document keeps it. */
export const deleteCookLogEntryInCloud = async (
  userId: string,
  recipeId: string,
  entryId: string,
  householdId?: string | null
): Promise<void> => {
  if (!isFirebaseEnabled() || !firestore) {
    throw new Error('Firebase not enabled');
  }
  const docRef = getCookLogDocRef(userId, recipeId, householdId);
  const snap = await getDoc(docRef);
  if (!snap.exists()) return;
  const data = snap.data() as FirestoreCookLogDoc;
  const legacy = Array.isArray(data.entries) ? data.entries : [];
  const inLegacy = legacy.some(e => e?.id === entryId);
  const inMap = !!data.byId && entryId in data.byId;
  if (!inLegacy && !inMap) return;

  // FieldPath, not a dotted string: an entry id is not guaranteed to be a
  // valid unquoted field name.
  const args: unknown[] = [new FieldPath('byId', entryId), deleteField(), 'updatedAt', serverTimestamp()];
  if (inLegacy) args.push('entries', legacy.filter(e => e?.id !== entryId));
  await (updateDoc as (...a: unknown[]) => Promise<void>)(docRef, ...args);
};

/**
 * Fetch every recipe's cook-log entries under the active scope, keyed by recipeId.
 */
export const fetchAllCookLogFromCloud = async (
  userId: string,
  householdId?: string | null
): Promise<Record<string, CookLogEntry[]>> => {
  if (!isFirebaseEnabled() || !firestore) {
    throw new Error('Firebase not enabled');
  }
  const collectionRef = getCookLogCollection(userId, householdId);
  const snapshot = await getDocs(collectionRef);
  const result: Record<string, CookLogEntry[]> = {};
  snapshot.docs.forEach((d) => {
    result[d.id] = entriesOfCookLogDoc(d.data() as FirestoreCookLogDoc);
  });
  return result;
};
